/** Source references are captured by the activation/review surface (C0 WP8). */
export type MedicationSourceEvidence = {
  source: 'ENRMC' | 'PAPER' | 'PACK';
  chart_reference: string;
  chart_date: string;
  prescriber_name: string;
  prescriber_number: string;
  image_object_id?: string;
};

const UPDATE_ALIASES: Readonly<Record<string, string>> = {
  endDate: 'end_date',
  sideEffects: 'side_effects',
  isPsychotropic: 'is_psychotropic',
  isAntipsychotic: 'is_antipsychotic',
  lastReviewDate: 'last_review_date',
  nextReviewDate: 'next_review_date',
  reviewerId: 'reviewer_id',
  isPrn: 'is_prn',
  prnMaxDoses: 'prn_max_doses',
  maxDailyDoses: 'max_daily_doses',
  maxDailyQuantity: 'max_daily_quantity',
  sourceEvidence: 'source_evidence',
  idempotencyKey: 'idempotency_key',
};

/** Preserve unknown keys so the database refuses them instead of silently dropping a requested edit. */
export function medicationOrderUpdatePayload(updates: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(updates).map(([key, value]) => [UPDATE_ALIASES[key] ?? key, value]),
  );
}

const REFUSAL_COPY: Readonly<Record<string, string>> = {
  c0_order_eligible_clinician_required:
    'An eligible registered nurse or clinical lead must review this medication change. Community activation must first be configured by your provider.',
  c0_order_source_evidence_required:
    'Add the chart reference, date and prescriber details before saving this medication change.',
  c0_order_source_image_required:
    'Attach the paper chart or pharmacy pack image from this provider before saving.',
  c0_order_terminal:
    'This order has ended. Create a new order for clinical review to restart treatment.',
  c0_order_field_not_allowed:
    'These medication details cannot be changed here. Check the order with your clinical lead.',
  c0_order_activation_rpc_required:
    'Use the medication activation review to attach source evidence before activating this order.',
  c0_order_active_demotion_refused:
    'An active order can be held or ceased by an eligible clinician. It cannot return to draft or pending review.',
  c0_order_insulin_locked:
    'An eligible clinician must confirm the insulin classification with source evidence.',
  c0_order_reason_required:
    'Enter a reason for holding, ceasing or removing this medication order.',
  c0_order_immediate_cease_only: 'Review the cease date. This action ceases the order today.',
  c0_order_replay_conflict:
    'This request was already used for different details. Reload the medication before trying again.',
  c0_order_controlled_writer_required: 'Use the medication review controls to save this change.',
};

/** Match exact controlled tokens only; database details and clinical narratives never become copy. */
export function medicationOrderRefusalMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== 'string') return null;
  return REFUSAL_COPY[message.trim().split(':', 1)[0] ?? ''] ?? null;
}
